import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { CustomerForm } from "./CustomerForm";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { USER_DIRECTORY_FIELDS, USERS_DIRECTORY_VIEW, USERS_TABLE } from "@/lib/userAccess";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function CustomerList({ readOnly = false }) {
  const [customers, setCustomers] = useState([]);
  const [editingCustomers, setEditingCustomers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState(null);
  const [userCodRep, setUserCodRep] = useState(null);
  const [canViewAllCustomers, setCanViewAllCustomers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [representatives, setRepresentatives] = useState({});
  const { toast } = useToast();

  const hasIncompleteSepaMandate = (customer) => Boolean(customer?.sepa_signature) && (
    !customer?.iban?.trim() ||
    !customer?.street?.trim() ||
    !customer?.city?.trim() ||
    !customer?.province?.trim() ||
    !customer?.postalCode?.trim()
  );

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await checkUserRole();
        await fetchRepresentatives();
        await fetchCustomers();
      } catch (error) {
        console.error('Error inicializando datos:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los datos iniciales",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    initializeData();
  }, [userRole, userCodRep, canViewAllCustomers]);

  const checkUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      const { data: userData, error } = await supabase
        .from(USERS_TABLE)
        .select('role, cod_rep, ver_comisiones_globales')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      if (userData) {
        setUserRole(userData.role);
        setUserCodRep(userData.cod_rep);
        setCanViewAllCustomers(userData.role === 'admin' || !!userData.ver_comisiones_globales);
      } else {
        setUserRole('cliente');
        setUserCodRep(null);
        setCanViewAllCustomers(false);
      }
    } catch (error) {
      console.error('Error verificando rol:', error);
      setUserRole('cliente');
    }
  };

  const fetchRepresentatives = async () => {
    try {
      const { data, error } = await supabase
        .from(USERS_DIRECTORY_VIEW)
        .select(USER_DIRECTORY_FIELDS)
        .not('cod_rep', 'is', null);

      if (error) throw error;

      const repsMap = {};
      data.forEach(rep => {
        if (rep.cod_rep) {
          repsMap[rep.cod_rep] = rep.name;
        }
      });
      setRepresentatives(repsMap);
    } catch (error) {
      console.error('Error fetching representatives:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      let query = supabase
        .from('customers')
        .select('*')
        .order('name', { ascending: true });

      // Si no es admin y no puede ver todos los clientes, filtrar por cod_rep
      if (userRole !== 'admin' && !canViewAllCustomers) {
        // Si el vendedor no tiene cod_rep, no devolver resultados
        if (!userCodRep) {
          setCustomers([]);
          return;
        }
        query = query.eq('cod_rep', userCodRep);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Filtro adicional de seguridad
      let filteredData = data || [];
      if (userRole !== 'admin' && !canViewAllCustomers) {
        filteredData = userCodRep ? filteredData.filter(customer => customer.cod_rep === userCodRep) : [];
      }

      setCustomers(filteredData);
    } catch (error) {
      console.error('Error en fetchCustomers:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los clientes",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (customerData) => {
    try {
      // Si es representante, asegurar que el cod_rep sea el suyo
      if (userRole === 'vendedor') {
        customerData.cod_rep = userCodRep;
      }

      if (customerData.id) {
        // Si es representante, verificar que está editando su propio cliente
        const originalCustomer = customers.find(c => c.id === customerData.id);
        if (userRole === 'vendedor' && originalCustomer.cod_rep !== userCodRep) {
          throw new Error('No tienes permiso para editar este cliente');
        }

        // Si es representante, mantener el cod_rep original
        if (userRole === 'vendedor') {
          customerData.cod_rep = originalCustomer.cod_rep;
        }

        const { error } = await supabase
          .from('customers')
          .update(customerData)
          .eq('id', customerData.id);

        if (error) throw error;

        toast({
          title: "Cliente actualizado",
          description: "El cliente se ha actualizado exitosamente",
        });
      } else {
        // Para nuevos clientes, si es representante, asignar su cod_rep
        if (userRole === 'vendedor') {
          customerData.cod_rep = userCodRep;
        }

        const { error } = await supabase
          .from('customers')
          .insert(customerData);

        if (error) throw error;

        toast({
          title: "Cliente creado",
          description: "El cliente se ha creado exitosamente",
        });
      }

      await fetchCustomers();
      
      // Cerrar el acordeón de edición
      setEditingCustomers(prev => {
        const newState = { ...prev };
        // Si es un cliente existente, eliminar su entrada de edición
        if (customerData.id) {
          delete newState[customerData.id];
        } else {
          // Si es un nuevo cliente, cerrar el formulario de nuevo cliente
          delete newState['new'];
        }
        return newState;
      });
    } catch (error) {
      console.error('Error guardando cliente:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el cliente",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (customer) => {
    // Si es representante, verificar que está editando su propio cliente
    if (userRole === 'vendedor' && customer.cod_rep !== userCodRep) {
      toast({
        title: "Error",
        description: "No tienes permiso para editar este cliente",
        variant: "destructive"
      });
      return;
    }
    setEditingCustomers(prev => ({
      ...prev,
      [customer.id]: customer
    }));
  };

  const handleCancelEdit = (customerId) => {
    setEditingCustomers(prev => {
      const newState = { ...prev };
      delete newState[customerId];
      return newState;
    });
  };

  const handleNewCustomer = () => {
    setEditingCustomers(prev => ({
      ...prev,
      'new': null
    }));
  };

  const handleDelete = async (customerId) => {
    // Buscar el cliente para mostrar su nombre en la confirmación
    const customer = customers.find(c => c.id === customerId);
    const customerName = customer?.name || 'este cliente';
    
    // Mostrar confirmación antes de eliminar
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres eliminar a ${customerName}?\n\nEsta acción no se puede deshacer.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      // Si es representante, verificar que está eliminando su propio cliente
      if (userRole === 'vendedor') {
        if (customer.cod_rep !== userCodRep) {
          throw new Error('No tienes permiso para eliminar este cliente');
        }
      }

      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      if (error) throw error;

      await fetchCustomers();
      toast({
        title: "Cliente eliminado",
        description: "El cliente se ha eliminado exitosamente",
        variant: "destructive"
      });
    } catch (error) {
      console.error('Error eliminando cliente:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el cliente",
        variant: "destructive"
      });
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Cargando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        {userRole === 'vendedor' && !canViewAllCustomers && !userCodRep && (
          <Alert className="bg-yellow-50 border-yellow-200">
            <AlertDescription>
              Tu usuario no tiene un código de representante asignado. No se mostrarán clientes hasta que se asigne uno.
            </AlertDescription>
          </Alert>
        )}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Gestión de Clientes</h2>
          {!readOnly && (
            <Button onClick={handleNewCustomer}>
              <Plus className="mr-2 h-4 w-4" /> Nuevo Cliente
            </Button>
          )}
        </div>

        <div className="relative">
          <Input
            type="text"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10"
          />
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={() => setSearchTerm("")}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          )}
        </div>

        {editingCustomers['new'] !== undefined && (
          <CustomerForm
            onSubmit={handleSubmit}
            onCancel={() => handleCancelEdit('new')}
            userCodRep={userCodRep}
            canViewAllCustomers={canViewAllCustomers}
          />
        )}

        {filteredCustomers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'No se encontraron clientes con ese criterio de búsqueda' : 'No hay clientes registrados'}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredCustomers.map((customer) => (
              <motion.div
                key={customer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-4 rounded-lg shadow-sm"
              >
                {editingCustomers[customer.id] ? (
                  <CustomerForm
                    customer={customer}
                    onSubmit={handleSubmit}
                    onCancel={() => handleCancelEdit(customer.id)}
                    userCodRep={userCodRep}
                    canViewAllCustomers={canViewAllCustomers}
                  />
                ) : (
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{customer.name}</h3>
                        {!customer.sepa_signature && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Falta firmar documento SEPA</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {hasIncompleteSepaMandate(customer) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Mandato SEPA firmado con datos incompletos</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {customer.incidencias?.trim() && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className="h-4 w-4 text-red-600" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Cliente con incidencias</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      {customer.email && (
                        <p className="text-sm text-gray-600">Email: {customer.email}</p>
                      )}
                      {customer.titular && (
                        <p className="text-sm text-gray-600">Titular: {customer.titular}</p>
                      )}
                      {customer.phone && (
                        <p className="text-sm text-gray-600">Teléfono: {customer.phone}</p>
                      )}
                      {customer.movil && (
                        <p className="text-sm text-gray-600">Móvil: {customer.movil}</p>
                      )}
                      {customer.cif && (
                        <p className="text-sm text-gray-600">CIF/NIF: {customer.cif}</p>
                      )}
                      {customer.address && (
                        <p className="text-sm text-gray-600">Dirección: {customer.address}</p>
                      )}
                      {customer.cod_rep && (
                        <p className="text-sm text-gray-600">
                          Representante: {customer.cod_rep}
                          {representatives[customer.cod_rep] && (
                            <span className="ml-1 text-gray-500">
                              ({representatives[customer.cod_rep]})
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    {!readOnly && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(customer)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={() => handleDelete(customer.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
